import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Landmark, Users, BadgeCheck, AlertTriangle, CheckCircle2, Layers, Scale, Split, PieChart, Vote, ArrowLeftRight, Flame, ReceiptText,
  Calculator, ChevronDown,
} from "lucide-react";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Container, IconTile, ProductFrame, Reveal, SectionHeading, useCopy } from "./shared";

/* --------------------------------- problem --------------------------------- */

export function ProblemSection() {
  const { c } = useCopy();
  return (
    <section id="problem" className="scroll-mt-20 py-16 sm:py-20" data-testid="landing-problem-section">
      <Container className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
        <Reveal><SectionHeading eyebrow={c.problem.eyebrow} title={c.problem.title} subtitle={c.problem.body} /></Reveal>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
          {c.problem.stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <div className="lp-card flex items-center gap-4 rounded-2xl p-4" data-testid={`landing-problem-stat-${i + 1}`}>
                <IconTile icon={AlertTriangle} className="h-11 w-11 bg-warning-soft text-warning" size={20} />
                <div><div className="text-xl font-semibold tracking-tight tabular-nums">{s.value}</div><div className="text-sm text-muted-foreground">{s.label}</div></div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ------------------------------- how it works ------------------------------ */

const LIFECYCLE_ICONS = [Layers, Scale, Split, PieChart, Vote, ArrowLeftRight, Flame, ReceiptText];

/** The eight-stage lifecycle, folded into "How it works" (committee R-9) and closed by default. */
function LifecycleDisclosure({ c }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-10" data-testid="landing-lifecycle">
      <CollapsibleTrigger asChild>
        <button type="button" className="lp-focus flex w-full items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-start text-sm font-semibold transition-colors hover:bg-accent/50 sm:px-5" data-testid="landing-lifecycle-toggle" aria-expanded={open}>
          <span className="flex items-center gap-3"><IconTile icon={Layers} className="h-8 w-8 rounded-lg" size={15} />{open ? c.how.lifecycleHide : c.how.lifecycleShow}</span>
          <ChevronDown size={16} className={cn("shrink-0 text-muted-foreground transition-transform duration-200", open && "rotate-180")} aria-hidden />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4" data-testid="landing-lifecycle-content">
        <p className="lp-body mb-4 text-sm leading-6 text-muted-foreground">{c.lifecycle.subtitle}</p>
        <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label={c.lifecycle.title}>
          {c.lifecycle.steps.map((s, i) => {
            const I = LIFECYCLE_ICONS[i];
            return (
              <li key={s.title} className="lp-card flex h-full flex-col rounded-2xl p-4" data-testid={`landing-lifecycle-step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary font-mono text-xs font-semibold text-primary-foreground">{i + 1}</span>
                  <span className="rounded-full border border-border bg-surface-3 px-2 py-0.5 text-2xs font-semibold text-muted-foreground">{s.chip}</span>
                </div>
                <div className="mt-3 flex items-start gap-3">
                  <IconTile icon={I} className="h-9 w-9" size={16} />
                  <div><h4 className="text-sm font-semibold leading-snug">{s.title}</h4><p className="mt-1 text-xs leading-relaxed text-muted-foreground">{s.body}</p></div>
                </div>
              </li>
            );
          })}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function HowItWorks() {
  const { c } = useCopy();
  return (
    <section id="how" className="scroll-mt-20 bg-surface-2 py-16 sm:py-20" data-testid="landing-how-it-works-section">
      <Container>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.4fr]">
          <Reveal>
            <div className="lg:sticky lg:top-24">
              <SectionHeading eyebrow={c.how.eyebrow} title={c.how.title} subtitle={c.how.subtitle} />
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="rounded-xl" data-testid="landing-how-create-account-button"><Link to="/auth?mode=register" onClick={() => track("landing_cta_click", { placement: "how" })}>{c.header.create}</Link></Button>
                <Button asChild variant="outline" className="rounded-xl" data-testid="landing-how-model-holding-button"><Link to="/model" onClick={() => track("landing_secondary_click", { placement: "how" })}><Calculator size={15} className="me-2" />{c.how.cta}</Link></Button>
              </div>
            </div>
          </Reveal>
          <ol className="relative space-y-4 border-s border-border ps-8">
            {c.how.steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <li className="lp-card relative rounded-2xl p-5" data-testid={`landing-how-step-${i + 1}`}>
                  <span className="absolute -start-[2.65rem] top-5 flex h-8 w-8 items-center justify-center rounded-full border-4 border-surface-2 bg-primary font-mono text-xs font-semibold text-primary-foreground">{i + 1}</span>
                  <h3 className="text-base font-semibold">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
        <Reveal delay={0.1}><LifecycleDisclosure c={c} /></Reveal>
      </Container>
    </section>
  );
}

/* --------------------------------- personas -------------------------------- */

const PERSONA_ICONS = { "co-owners": Users, "pool-managers": Landmark, valuators: BadgeCheck };

function PersonaPanel({ p, c }) {
  const I = PERSONA_ICONS[p.id] || Users;
  return (
    <article className="lp-card rounded-2xl p-5 sm:p-6" data-testid={`landing-persona-panel-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[var(--shadow-sm)]"><I size={22} strokeWidth={1.75} /></span>
          <div><h3 className="text-lg font-semibold leading-snug sm:text-xl">{p.title}</h3><p className="lp-body mt-1 text-sm leading-6 text-muted-foreground">{p.tagline}</p></div>
        </div>
        <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/5 text-2xs font-semibold text-primary">{p.role}</Badge>
      </div>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <div>
          <div className="lp-eyebrow mb-2 flex items-center gap-2 text-warning"><AlertTriangle size={12} />{c.personas.painsLabel}</div>
          <ul className="space-y-2">
            {p.pains.map((t) => <li key={t} className="flex gap-2 rounded-xl border border-warning-border bg-warning-soft px-3 py-2 text-sm leading-relaxed"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-warning" />{t}</li>)}
          </ul>
        </div>
        <div>
          <div className="lp-eyebrow mb-2 flex items-center gap-2 text-success"><CheckCircle2 size={12} />{c.personas.solutionsLabel}</div>
          <ul className="space-y-2">
            {p.solutions.map((t) => <li key={t} className="flex gap-2 rounded-xl border border-success-border bg-success-soft px-3 py-2 text-sm leading-relaxed"><CheckCircle2 size={14} className="mt-0.5 shrink-0 text-success" />{t}</li>)}
          </ul>
        </div>
      </div>
    </article>
  );
}

/** Who it serves — one panel at a time behind role tabs (committee R-9), instead of three tall cards. */
export function PersonaSection() {
  const { c } = useCopy();
  const items = c.personas.items;
  return (
    <section id="personas" className="scroll-mt-20 py-16 sm:py-20" data-testid="landing-personas-section">
      <Container>
        <Reveal><SectionHeading eyebrow={c.personas.eyebrow} title={c.personas.title} subtitle={c.personas.subtitle} align="center" /></Reveal>
        <Reveal delay={0.06} className="mt-10">
          <Tabs defaultValue={items[0].id} className="w-full">
            <TabsList className="mx-auto grid h-auto w-full max-w-xl grid-cols-3 rounded-xl p-1" data-testid="landing-persona-tabs">
              {items.map((p) => <TabsTrigger key={p.id} value={p.id} className="min-h-11 rounded-lg text-sm sm:min-h-10" data-testid={`landing-persona-tab-${p.id}`}>{p.tab}</TabsTrigger>)}
            </TabsList>
            {items.map((p) => <TabsContent key={p.id} value={p.id} className="mt-6 focus-visible:ring-0"><PersonaPanel p={p} c={c} /></TabsContent>)}
          </Tabs>
        </Reveal>
      </Container>
    </section>
  );
}

/* ------------------------------ product screens ----------------------------- */

/** Real screens from the showcase holding (committee R-7): the frame follows language and theme; images lazy-load. */
export function ScreensSection() {
  const { c } = useCopy();
  const items = c.screens.items;
  return (
    <section id="screens" className="scroll-mt-20 bg-surface-2 py-16 sm:py-20" data-testid="landing-screens-section">
      <Container>
        <Reveal><SectionHeading eyebrow={c.screens.eyebrow} title={c.screens.title} subtitle={c.screens.subtitle} /></Reveal>
        <Reveal delay={0.06} className="mt-10">
          <Tabs defaultValue={items[0].k} className="w-full">
            <TabsList className="grid h-auto w-full max-w-md grid-cols-3 rounded-xl p-1" data-testid="landing-screens-tabs">
              {items.map((s) => <TabsTrigger key={s.k} value={s.k} className="min-h-11 rounded-lg text-sm sm:min-h-10" data-testid={`landing-screens-tab-${s.k}`}>{s.tab}</TabsTrigger>)}
            </TabsList>
            {items.map((s) => (
              <TabsContent key={s.k} value={s.k} className="mt-6 focus-visible:ring-0" data-testid={`landing-screens-panel-${s.k}`}>
                <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_1.8fr] lg:gap-10">
                  <div>
                    <h3 className="text-xl font-semibold leading-snug">{s.title}</h3>
                    <p className="lp-body mt-3 text-sm leading-7 text-muted-foreground sm:text-base">{s.body}</p>
                    <p className="mt-4 text-xs text-muted-foreground" data-testid="landing-screens-note">{c.screens.note}</p>
                  </div>
                  <ProductFrame screen={s.k} alt={s.alt} />
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </Reveal>
      </Container>
    </section>
  );
}
